import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { LifeBuoy, Plus, Flame, ChevronUp, ChevronDown, Minus } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  PeriodFilter,
  SearchField,
  StatusCell,
  StatusBadge,
  DateCell,
  ActionsMenuCell,
  TABLE_PRESETS,
  type StatusTone,
} from '@afrilink/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useTickets, type Ticket, type TicketStatus, type TicketPriority } from '@/domains/support';

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'Open',
  pending_customer: 'Awaiting your reply',
  closed: 'Closed',
};

const STATUS_TONE: Record<TicketStatus, StatusTone> = {
  open: 'info',
  pending_customer: 'pending',
  closed: 'draft',
};

const PRIORITY_TONE: Record<TicketPriority, StatusTone> = {
  low: 'draft',
  normal: 'info',
  high: 'pending',
  urgent: 'overdue',
};

const PRIORITY_ICON: Record<
  TicketPriority,
  React.ComponentType<{ className?: string }>
> = {
  low: ChevronDown,
  normal: Minus,
  high: ChevronUp,
  urgent: Flame,
};

const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'pending_customer', label: 'Awaiting your reply' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
] as const;

type FilterKey = (typeof FILTERS)[number]['key'];

export default function TicketsListPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useTickets();
  const [filter, setFilter] = useState<FilterKey>('open');

  /**
   * A customer's own ticket list — STOCK, default `all-time` (Rule T-1), and
   * the date is labelled `Opened` rather than "Period": "open tickets in June"
   * is not a thing, "tickets opened in June" is a different question.
   *
   * `dateMode: 'client'` under AR-5 option (a) — `useTickets()` fetches this
   * customer's whole set, unlimited, and one customer has a handful of rows.
   */
  const table = useTableFilters<Ticket>({
    rows: data,
    dateMode: 'client',
    dateLabel: 'Opened',
    dateOf: (t) => t.openedAt,
    searchText: (t) => [t.number, t.subject].join(' '),
    facets: useMemo(
      () => [(t: Ticket) => filter === 'all' || t.status === filter],
      [filter],
    ),
  });

  const visible = table.rows;

  const columns: UniversalTableColumn<Ticket>[] = [
    {
      key: 'number',
      header: 'Ticket',
      sortValue: (t) => t.number,
      render: (t) => (
        <span className="text-sm font-semibold tabular-nums text-navy-primary">
          {t.number}
        </span>
      ),
      width: 'w-40',
    },
    {
      key: 'subject',
      header: 'Subject',
      sortValue: (t) => t.subject,
      render: (t) => (
        <span className="block truncate font-medium text-navy-primary">{t.subject}</span>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      sortValue: (t) => t.priority,
      render: (t) => {
        const Icon = PRIORITY_ICON[t.priority];
        return (
          <StatusBadge tone={PRIORITY_TONE[t.priority]}>
            <span className="inline-flex items-center gap-1">
              <Icon className="size-3" aria-hidden />
              {t.priority}
            </span>
          </StatusBadge>
        );
      },
      width: 'w-32',
      hideOnMobile: true,
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (t) => t.status,
      render: (t) => <StatusCell tone={STATUS_TONE[t.status]} label={STATUS_LABEL[t.status]} />,
      width: 'w-32',
    },
    {
      key: 'lastReplyAt',
      header: 'Last reply',
      sortValue: (t) => t.lastReplyAt,
      render: (t) => <DateCell value={t.lastReplyAt} format="relative" />,
      hideOnMobile: true,
      width: 'w-32',
    },
    {
      key: 'actions',
      header: '',
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
          <ActionsMenuCell
            ariaLabel={`Actions for ${t.number}`}
            items={[
              { label: 'Open ticket', onSelect: () => navigate(`/app/support/${t.id}`) },
            ].filter(Boolean) as React.ComponentProps<typeof ActionsMenuCell>['items']}
          />
        </div>
      ),
      width: 'w-12',
      align: 'right',
    },
  ];

  return (
    <UniversalPage
      title="Support tickets"
      description="Your open and recent support requests."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Support', href: '/app/support' },
        { label: 'Tickets' },
      ]}
      actions={
        <Button size="sm" onClick={() => navigate('/app/support/new')}>
          <Plus className="h-4 w-4" />
          New ticket
        </Button>
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <nav className="flex items-center gap-1" aria-label="Filter tickets">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              className={
                'rounded px-3 py-1.5 text-xs font-medium transition-colors ' +
                (filter === f.key
                  ? 'bg-navy-primary text-background'
                  : 'bg-muted text-foreground-soft hover:bg-muted')
              }
            >
              {f.label}
            </button>
          ))}
        </nav>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Opened"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search ticket number or subject"
            className="w-full sm:w-72"
          />
        </div>
      </div>

      <UniversalTable
        data={visible}
        columns={columns}
        getRowKey={(t) => t.id}
        onRowClick={(t) => navigate(`/app/support/${t.id}`)}
        isLoading={isLoading}
        error={error}
        empty={{
          icon: LifeBuoy,
          title:
            table.q || filter !== 'open' || table.filters.activeCount > 0
              ? 'No matching tickets'
              : 'No open tickets',
          description:
            table.q || filter !== 'open' || table.filters.activeCount > 0
              ? 'Try a different filter, widen the period, or clear the search.'
              : 'Open a ticket if you need help with hosting, domains, or billing.',
          action: (
            <Button size="sm" onClick={() => navigate('/app/support/new')}>
              <Plus className="h-4 w-4" />
              New ticket
            </Button>
          ),
        }}
        caption={!isLoading && !error ? table.caption : undefined}
      />
    </UniversalPage>
  );
}
